import React, { useState, useEffect, useRef } from 'react';
import { shopModel, kioskModel } from '../Axios/AxiosConfig';
import { encryptBody } from '../Axios/Middleware';

const IDC_ORIGIN = process.env.REACT_APP_IDC_URL || '*';

export default function IDCPaymentBridge() {
  const params = new URLSearchParams(window.location.search);
  const appointment_id = params.get('appointment_id');
  const amount = params.get('amount') || '200';

  const [status, setStatus] = useState('idle'); // idle | loading | paying | success | failed | cancelled
  const [errorMsg, setErrorMsg] = useState('');

  // Guard: if no appointment_id, show error
  useEffect(() => {
    if (!appointment_id) {
      setStatus('failed');
      setErrorMsg('Invalid payment link. No appointment ID provided.');
    }
  }, [appointment_id]);

  const hasAutoTriggered = useRef(false);

  // Auto-trigger PhonePe on initial load to skip the manual click step
  useEffect(() => {
    if (appointment_id && status === 'idle' && !hasAutoTriggered.current) {
      hasAutoTriggered.current = true;
      handlePay();
    }
  }, [appointment_id, status]);

  const handlePay = async () => {
    if (!appointment_id) return;
    setStatus('loading');
    setErrorMsg('');
    try {
      // Step 1: Generate PhonePe payment token (public route for IDC via Shop backend)
      const res = await shopModel.post(
        '/api/v1/PhonePe/generate-idc-token',
        encryptBody({
          amount: parseInt(amount, 10),
          metaInfo: {
            udf1: btoa(JSON.stringify({ Appointment_id: appointment_id })),
            udf2: 'idc-payment-bridge',
          },
        }, { includeIv: false })
      );

      if (!res.data || !res.data.paymentUrl) {
        throw new Error('Payment token not received from server.');
      }

      // Step 2: Verify PhonePe SDK is available
      if (
        typeof window === 'undefined' ||
        !window.PhonePeCheckout ||
        typeof window.PhonePeCheckout.transact !== 'function'
      ) {
        throw new Error('PhonePe SDK not loaded. Please refresh and try again.');
      }

      setStatus('paying');

      // Step 3: Open PhonePe IFRAME (same pattern as ConfirmSlot.jsx)
      const callback = async (responseStatus) => {
        if (responseStatus === 'CONCLUDED') {
          setStatus('loading');
          try {
            // Step 4: Complete the appointment via DiabetesAppointmentController
            // Note: UpdateAndCompleteAppointment now handles all notifications internally
            await kioskModel.post(
              '/api/v1/diabetes/UpdateAndCompleteAppointment',
              { Appointment_id: appointment_id }
            );
            setStatus('success');
            // Step 5: Notify IDC parent window
            window.parent.postMessage({ status: 'SUCCESS', appointment_id }, IDC_ORIGIN);
          } catch (err) {
            console.error('[IDCPaymentBridge] Post-payment error:', err);
            setStatus('failed');
            setErrorMsg('Payment was received but appointment confirmation failed. Please contact support.');
          }
        } else if (responseStatus === 'USER_CANCEL') {
          // Delete the pending appointment — same pattern as Aiwell ConfirmSlot.jsx DeleteAppointment
          await kioskModel.post('/api/v1/diabetes/DeleteInitiatedAppointment', { Appointment_id: appointment_id })
            .catch(err => console.warn('[IDCPaymentBridge] Cancel cleanup failed (non-fatal):', err.message));
          setStatus('cancelled');
          window.parent.postMessage({ status: 'CANCELLED' }, IDC_ORIGIN);
        } else {
          console.warn('[IDCPaymentBridge] Unhandled PhonePe status:', responseStatus);
          // Also delete on any unhandled failure
          await kioskModel.post('/api/v1/diabetes/DeleteInitiatedAppointment', { Appointment_id: appointment_id })
            .catch(err => console.warn('[IDCPaymentBridge] Cancel cleanup failed (non-fatal):', err.message));
          setStatus('cancelled');
          window.parent.postMessage({ status: 'CANCELLED' }, IDC_ORIGIN);
        }
      };

      window.PhonePeCheckout.transact({ tokenUrl: res.data.paymentUrl, type: 'IFRAME', callback });
    } catch (err) {
      console.error('[IDCPaymentBridge] Error initiating payment:', err);
      setStatus('failed');
      setErrorMsg(err.message || 'Something went wrong. Please try again.');
    }
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #0d1f17 0%, #122b1e 50%, #0d1f17 100%)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontFamily: "'Inter', 'Segoe UI', sans-serif", padding: '24px',
    }}>
      <div style={{
        background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(232,243,239,0.12)',
        borderRadius: '16px', padding: '36px 32px', width: '100%', maxWidth: '380px',
        textAlign: 'center', boxShadow: '0 8px 40px rgba(0,0,0,0.4)',
      }}>
        <div style={{ marginBottom: '8px' }}>
          <span style={{ fontSize: '11px', fontWeight: 600, letterSpacing: '0.12em', color: '#DA6E0B', textTransform: 'uppercase' }}>
            Ayur.AI Integrative Diabetes Center
          </span>
        </div>
        <h2 style={{ color: '#F3FAF7', fontSize: '20px', fontWeight: 700, margin: '0 0 6px' }}>Secure Payment</h2>
        <p style={{ color: 'rgba(226,239,234,0.6)', fontSize: '13px', margin: '0 0 28px' }}>Complete your consultation booking</p>
        <div style={{ background: 'rgba(218,110,11,0.1)', border: '1px solid rgba(218,110,11,0.25)', borderRadius: '10px', padding: '16px', marginBottom: '24px' }}>
          <div style={{ color: 'rgba(226,239,234,0.6)', fontSize: '12px', marginBottom: '4px' }}>
            <span style={{ background: 'rgba(218,110,11,0.2)', color: '#DA6E0B', padding: '2px 8px', borderRadius: '4px', fontSize: '10px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Launch Offer</span>
            {' '}Consultation Fee
          </div>
          <div style={{ color: '#F3FAF7', fontSize: '32px', fontWeight: 800 }}>Rs.{amount}</div>
        </div>

        {status === 'idle' && <div style={{ color: 'rgba(226,239,234,0.6)', fontSize: '14px', padding: '14px 0' }}>Initializing secure payment...</div>}
        {status === 'loading' && <div style={{ color: 'rgba(226,239,234,0.6)', fontSize: '14px', padding: '14px 0' }}>Processing...</div>}
        {status === 'paying' && <div style={{ color: 'rgba(226,239,234,0.6)', fontSize: '13px', padding: '14px 0' }}>Complete your payment in the PhonePe window above.</div>}
        {status === 'success' && <div style={{ color: '#4caf50', fontSize: '14px', fontWeight: 600 }}>Payment successful! Your appointment is confirmed.</div>}
        {status === 'cancelled' && (
          <div>
            <div style={{ color: '#DA6E0B', fontSize: '13px', marginBottom: '16px' }}>Payment was cancelled.</div>
            <button onClick={handlePay} style={{ padding: '10px 24px', background: 'rgba(218,110,11,0.15)', border: '1px solid rgba(218,110,11,0.4)', borderRadius: '8px', color: '#DA6E0B', fontSize: '13px', cursor: 'pointer' }}>Try Again</button>
          </div>
        )}
        {status === 'failed' && (
          <div>
            <div style={{ color: '#f44336', fontSize: '13px', marginBottom: '16px' }}>{errorMsg || 'Payment failed. Please try again.'}</div>
            <button onClick={handlePay} style={{ padding: '10px 24px', background: 'rgba(244,67,54,0.1)', border: '1px solid rgba(244,67,54,0.4)', borderRadius: '8px', color: '#f44336', fontSize: '13px', cursor: 'pointer' }}>Retry Payment</button>
          </div>
        )}
        <div style={{ marginTop: '24px', color: 'rgba(226,239,234,0.3)', fontSize: '11px' }}>Secured by PhonePe | Powered by Ayur.AI</div>
      </div>
    </div>
  );
}